import type {
	LWApiPublicAssetControlPanelCredentialsSetParamsRaw,
	LWApiPublicAssetControlPanelCredentialsSetResultRaw,
} from '@liquidweb-dev/club-sauce/public/asset/controlPanel/credentials/index.raw';
import { type LWApiPublicSslCertificateHostServersResultRaw } from '@liquidweb-dev/club-sauce/public/ssl/certificate/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from 'modules/queries/api';
import { ASSET_CONTROL_PANEL_CREDENTIALS_USERNAME_PASSWORD } from 'modules/queries/asset/controlPanel/credentials/useUsernameAndPassword';
import { SERVER_WHM_DNS_LISTZONES_QUERY_KEY } from 'modules/queries/server/whm/dns/useListzones';
import { SSL_CERTIFICATE_HOST_SERVERS_QUERY_KEY } from 'modules/queries/ssl/certificate/useHostSevers';

const useAssetControlPanelCredentialsSet = () => {
	const queryClient = useQueryClient();

	return useMutation<
		LWApiPublicAssetControlPanelCredentialsSetResultRaw,
		ApiErrorI,
		LWApiPublicAssetControlPanelCredentialsSetParamsRaw
	>({
		mutationFn: (params) =>
			api.asset.controlPanel.credentials.apiRaw.set(params),

		onSuccess: (result) => {
			queryClient.invalidateQueries({
				queryKey: [ASSET_CONTROL_PANEL_CREDENTIALS_USERNAME_PASSWORD],
			});
			queryClient.invalidateQueries({
				queryKey: [SSL_CERTIFICATE_HOST_SERVERS_QUERY_KEY],
			});
			queryClient.invalidateQueries({
				queryKey: [
					SERVER_WHM_DNS_LISTZONES_QUERY_KEY,
					{ uniq_id: result.uniq_id },
				],
			});

			// pessimistic update
			queryClient.setQueriesData<LWApiPublicSslCertificateHostServersResultRaw>(
				[SSL_CERTIFICATE_HOST_SERVERS_QUERY_KEY],
				(current) => {
					if (!current) {
						return current;
					}

					// updates for WHM
					if (result.control_panel === 'whm') {
						const [updatedHostServerDomain, updatedHostServerData] =
							Object.entries(current).find(
								([_, data]) => data.uniq_id === result.uniq_id,
							) || [];

						if (!updatedHostServerDomain || !updatedHostServerData) {
							return current;
						}

						return {
							...current,
							[updatedHostServerDomain]: {
								...updatedHostServerData,
								cpCredentials: 1,
								sniEnabled: 1, // assume SNI gets enabled when adding credentials - this is generally true
							},
						};
					}

					return current;
				},
			);
		},
	});
};

export default useAssetControlPanelCredentialsSet;
